import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Company } from "../types/company";
import { ApiResponse, CompanyApiResponse } from "../types/auth";

const CompareCompaniesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  useEffect(() => {
    const idsParam = searchParams.get("ids");
    if (!idsParam) {
      setError("比較する企業が選択されていません");
      setLoading(false);
      return;
    }

    const ids = idsParam.split(",").map((id) => parseInt(id.trim()));
    if (ids.length < 2) {
      setError("比較するには2社以上選択してください");
      setLoading(false);
      return;
    }

    // 企業データを取得
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/companies`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data: ApiResponse<CompanyApiResponse> | any =
            await response.json();

          const payload = data.data;
          let allCompanies: Company[] = [];

          if (Array.isArray(payload)) {
            allCompanies = payload;
          } else if (payload && Array.isArray(payload.companies)) {
            allCompanies = payload.companies;
          }

          // IDでフィルター
          const selectedCompanies = allCompanies.filter((c) =>
            ids.includes(c.id)
          );
          setCompanies(selectedCompanies);
        } else {
          setError("企業データの取得に失敗しました");
        }
      } catch (error) {
        setError("企業データの取得中にエラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [searchParams, API_BASE_URL]);

  const formatSalary = (min?: number | null, max?: number | null): string => {
    if (!min && !max) return "-";
    if (min && max) return `${min}万円 〜 ${max}万円`;
    if (min) return `${min}万円〜`;
    if (max) return `〜${max}万円`;
    return "-";
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-600">
        <div className="loading-spinner"></div>
        <span>読み込み中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate("/companies")}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          企業一覧に戻る
        </button>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-8">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg mb-4">
          選択された企業が見つかりませんでした
        </div>
        <button
          onClick={() => navigate("/companies")}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          企業一覧に戻る
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">企業比較</h1>
        <button
          onClick={() => navigate("/companies")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          企業一覧に戻る
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-4 border-b-2 border-gray-300 bg-gray-100 text-left text-base font-semibold text-gray-800 sticky left-0 z-10">
                項目
              </th>
              {companies.map((company) => (
                <th
                  key={company.id}
                  className="p-4 border-b-2 border-gray-300 bg-blue-50 text-left text-base font-semibold text-gray-800 min-w-[250px]"
                >
                  {company.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* 基本情報 */}
            <tr className="bg-gray-50">
              <td
                colSpan={companies.length + 1}
                className="p-3 border-b border-gray-200 font-bold text-gray-700 sticky left-0 z-10"
              >
                基本情報
              </td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                業界
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.industry || "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                志望度
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.interest_level
                    ? "★".repeat(company.interest_level) +
                      "☆".repeat(5 - company.interest_level)
                    : "-"}
                </td>
              ))}
            </tr>

            {/* 待遇・条件 */}
            <tr className="bg-gray-50">
              <td
                colSpan={companies.length + 1}
                className="p-3 border-b border-gray-200 font-bold text-gray-700 sticky left-0 z-10"
              >
                待遇・条件
              </td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                想定年収
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {formatSalary(company.salary_min, company.salary_max)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                平均年収
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.salary_ave ? `${company.salary_ave}万円` : "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                年間休日数
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.holidays ? `${company.holidays}日` : "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                従業員数
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.employee_count ? `${company.employee_count}人` : "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                勤務地
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.location || "-"}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                福利厚生
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600 whitespace-pre-wrap"
                >
                  {company.benefits || "-"}
                </td>
              ))}
            </tr>

            {/* リンク情報 */}
            <tr className="bg-gray-50">
              <td
                colSpan={companies.length + 1}
                className="p-3 border-b border-gray-200 font-bold text-gray-700 sticky left-0 z-10"
              >
                リンク情報
              </td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                ウェブサイト
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.website ? (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline break-all"
                    >
                      {company.website}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                マイページURL
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.mypage_url ? (
                    <a
                      href={company.mypage_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline break-all"
                    >
                      {company.mypage_url}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                マイページID
              </td>
              {companies.map((company) => (
                <td
                  key={company.id}
                  className="p-4 border-b border-gray-200 text-gray-600"
                >
                  {company.mypage_id || "-"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompareCompaniesPage;
